import { http } from "./http";
import type { FanCard } from "../types/fans";
import type { FanOrderRow } from "../lib/fanRequestsModel";
import type {
  Order,
  HistoryItem,
  Receipt,
  RefundPreview,
  EarningsResponse,
  MonthlyEarnings,
  FansResponse,
  FreeOrderResponse,
  BatchDeclineBody,
  BatchDeclineResponse,
  Insights,
  TrendingData,
  TopDJ,
} from "../types/orders";
import type { TrackHistoryUpload } from "../types/disputes";
import type { FeeTierStatus } from "../components/FeeTierSheet";

// Orders from both sides: the talent's queue and history, and the fan's
// view of one request.
// The newest /orders/earnings answer, kept ONLY for decoration that must not
// cost its own request (the tab bar's "money is waiting" bump: the page the
// talent lands on has just asked the same question). Money screens never read
// this — they always call ordersApi.earnings() for a fresh answer.
let lastEarnings: { at: number; res: EarningsResponse } | null = null;
export const recentEarnings = (maxAgeMs: number): EarningsResponse | null =>
  lastEarnings && Date.now() - lastEarnings.at <= maxAgeMs ? lastEarnings.res : null;

export const ordersApi = {
  // ── the talent's queue ──
  pending: () => http.get<Order[]>("/orders/pending"),
  accepted: () => http.get<Order[]>("/orders/accepted"),
  /** Awaiting-counter orders from the last 48h. */
  countered: () => http.get<Order[]>("/orders/countered"),
  /** Played (and, when asked, refunded) requests — history. */
  played: (opts: { includeRefunded?: boolean } = {}) =>
    http.get<HistoryItem[]>("/orders/played", opts.includeRefunded ? { params: { includeRefunded: "1" } } : undefined),
  declined: () => http.get<HistoryItem[]>("/orders/declined"),
  earnings: () =>
    http.get<EarningsResponse>("/orders/earnings").then((res) => {
      lastEarnings = { at: Date.now(), res };
      return res;
    }),
  monthlyEarnings: (tzOffset: number) => http.get<MonthlyEarnings>(`/orders/monthly-earnings?tzOffset=${tzOffset}`),
  fans: () => http.get<FansResponse>("/orders/fans"),
  /** Accept / play a stack of requests at once. */
  batch: (action: string, orderIds: string[]) => http.post<unknown>(`/orders/batch/${action}`, { orderIds }),
  batchDecline: (body: BatchDeclineBody) => http.post<BatchDeclineResponse>("/orders/batch/decline", body),
  withdrawCounter: (orderId: string) => http.post<unknown>(`/orders/${orderId}/withdraw-counter`),
  refundPreview: (orderId: string) => http.get<RefundPreview>(`/orders/${orderId}/refund-preview`),
  refund: (orderId: string) => http.post<unknown>(`/orders/${orderId}/refund`),
  /** The talent's commission tier (Profile). */
  feeTier: () => http.get<FeeTierStatus>("/orders/fee-tier"),
  /** Requests the talent hasn't seen yet — the tab-bar badge. */
  newRequests: () => http.get<Order[]>("/orders/new"),
  insights: (tzOffset: number) => http.get<Insights>(`/orders/insights?tzOffset=${tzOffset}`),
  /** What fans are asking for lately (the DJ leaderboard). */
  trending: (range: string) => http.get<TrendingData>(`/orders/trending?range=${range}`),
  fansLeaderboard: () => http.get<{ fans?: unknown[] }>("/orders/fans-leaderboard"),
  fanCard: (fanUserId: string) => http.get<FanCard>(`/orders/fans/${encodeURIComponent(fanUserId)}/card`),
  /** The public twin of /orders/top-djs — the fan app carries no DJ token. */
  topDjsPublic: () => http.get<{ djs?: TopDJ[] }>("/orders/top-djs-public"),
  /** The night's track-history file, attached to the played requests it covers. */
  uploadHistory: (file: FormData) =>
    http.post<TrackHistoryUpload>("/orders/upload-history", file, { headers: { "Content-Type": "multipart/form-data" } }),
  /** The same, for ONE played request — answers what it did to an open "Wasn't played?" claim. */
  uploadHistoryFor: (orderId: string, file: FormData) =>
    http.post<TrackHistoryUpload>(`/orders/${orderId}/upload-history`, file, { headers: { "Content-Type": "multipart/form-data" } }),

  // ── the fan's view ──
  status: (orderId: string) => http.get<FanOrderRow>(`/orders/order-status/${orderId}`),
  statusBatch: (orderIds: string[]) => http.get<{ orders: FanOrderRow[] }>(`/orders/status-batch?ids=${orderIds.join(",")}`),
  receipt: (receiptId: string) => http.get<Receipt>(`/orders/receipt/${encodeURIComponent(receiptId)}`),
  passCounter: (orderId: string) => http.post<unknown>(`/orders/${orderId}/pass-counter`),
  setUpdatesEmail: (orderId: string, email: string) => http.post<unknown>(`/orders/${orderId}/updates-email`, { email }),
  /** A $0 request (the talent's price is free) — no payment sheet. */
  createFree: (payload: Record<string, unknown>) => http.post<FreeOrderResponse>("/orders/free", payload),
};
